import React, { useEffect, useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, ConciergeBell, Plane, ChefHat, Anchor, ArrowRight } from "lucide-react";
import Seo, { websiteSchema, orgSchema, faqSchema } from "@/components/Seo";
import HeroSearch from "@/components/HeroSearch";
import VillaCard from "@/components/VillaCard";
import LazyVisible from "@/components/LazyVisible";
import { Button } from "@/components/ui/button";
import { searchVillas, getMapVillas, getReviews, getAreas, getBlog, getStats } from "@/lib/api";
import { AREAS, sizedImg } from "@/lib/constants";
import { useLanguage } from "@/context/LanguageContext";
import { HOME_FAQ_I18N } from "@/i18n/homeFaq";

// Heavy / below-the-fold chunks — kept out of the critical main bundle.
const MapView = lazy(() => import("@/components/MapView"));
const HomeReviews = lazy(() => import("@/components/HomeReviews"));
const FaqAccordion = lazy(() => import("@/components/FaqAccordion"));

const HERO_VIDEO = "https://videos.pexels.com/video-files/2169880/2169880-hd_1920_1080_30fps.mp4";
// Self-hosted, optimized WebP, served responsively (≈37KB mobile vs 155KB) so
// the LCP hero downloads fast and frees bandwidth for the JS bundle.
const HERO_POSTER = "/hero-villa-828.webp";
const HERO_SRCSET = "/hero-villa-828.webp 828w, /hero-villa-1280.webp 1280w, /hero-villa-1600.webp 1600w, /hero-villa.webp 1920w";

const SERVICE_ICONS = [ChefHat, Plane, Anchor, ConciergeBell];
const SERVICE_KEYS = [
  ["services.chefT", "services.chefD"],
  ["services.transferT", "services.transferD"],
  ["services.yachtT", "services.yachtD"],
  ["services.conciergeT", "services.conciergeD"],
];

const Section = ({ children, className = "" }) => (
  <section className={`container-lux py-16 sm:py-20 ${className}`}>{children}</section>
);

const Home = () => {
  const { t, localePath, locale } = useLanguage();
  const faqItems = HOME_FAQ_I18N[locale] || HOME_FAQ_I18N.en;

  // Defer the heavy hero video until the browser is idle so the poster image
  // (the LCP element) paints immediately and the video doesn't block load.
  // The HD video is ~12MB, so we ONLY load it on larger screens with a fast,
  // non-data-saver connection. Mobile / slow connections keep the lightweight
  // poster image (same first frame) — a huge mobile-performance win.
  const [showVideo, setShowVideo] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const conn = navigator.connection || {};
    const slow = conn.saveData || /(^|[^0-9])(2g|3g)$/i.test(conn.effectiveType || "");
    const bigScreen = window.matchMedia("(min-width: 1024px)").matches;
    if (!bigScreen || slow) return undefined; // keep poster only on mobile/slow

    let id;
    const start = () => setShowVideo(true);
    if (window.requestIdleCallback) {
      id = window.requestIdleCallback(start, { timeout: 2500 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    id = setTimeout(start, 1500);
    return () => clearTimeout(id);
  }, []);

  const { data: featured } = useQuery({ queryKey: ["homeVillas"], queryFn: () => searchVillas({ limit: 24 }) });
  const { data: mapVillas } = useQuery({ queryKey: ["mapVillas"], queryFn: getMapVillas });
  const { data: reviews } = useQuery({ queryKey: ["reviewsFeatured"], queryFn: () => getReviews({ featured: true }) });
  const { data: areas } = useQuery({ queryKey: ["areas"], queryFn: getAreas });
  const { data: blog } = useQuery({ queryKey: ["blogHome", locale], queryFn: () => getBlog({ limit: 3 }, locale) });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: getStats });

  return (
    <div>
      <Seo title="Luxury Villa Rentals in Marbella" description="Rent the finest luxury villas in Marbella with private pools, sea views and a dedicated concierge. A booking experience beyond Booking, Airbnb and VRBO." canonical="/" schema={[websiteSchema(), orgSchema(), faqSchema(faqItems)]} image="/hero-villa-1600.webp" />

      {/* HERO */}
      <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
        <img src={HERO_POSTER} srcSet={HERO_SRCSET} sizes="100vw" alt="Luxury villa in Marbella" fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
        {showVideo && (
          <video className="absolute inset-0 h-full w-full object-cover animate-fade-in" autoPlay muted loop playsInline preload="none" poster={HERO_POSTER}>
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,20,20,0.55) 0%, rgba(20,20,20,0.25) 40%, rgba(20,20,20,0.55) 100%)" }} />
        <div className="relative h-full container-lux flex flex-col justify-center items-center text-center">
          <span className="eyebrow text-white/80">{t("hero.eyebrow")}</span>
          <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.02] mt-3 max-w-4xl">
            {t("hero.title")}
          </h1>
          <p className="text-white/85 text-base sm:text-lg mt-5 max-w-2xl">
            {t("hero.subtitle")}
          </p>
          <div className="mt-9 w-full flex justify-center">
            <HeroSearch />
          </div>
        </div>
      </section>

      {/* STATS */}
      <div className="border-b border-border bg-white">
        <div className="container-lux grid grid-cols-2 md:grid-cols-4 gap-6 py-8 text-center">
          {[[stats?.villas || "23", t("stats.villas")], ["7", t("stats.areas")], [`${stats?.avg_rating || "4.9"}★`, t("stats.rating")], [`${(stats?.guests_served || 12500).toLocaleString()}+`, t("stats.guests")]].map(([n, l]) => (
            <div key={l}>
              <div className="font-display text-3xl sm:text-4xl">{n}</div>
              <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* FEATURED */}
      <Section>
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="eyebrow">{t("sections.featuredEyebrow")}</span>
            <h2 className="text-3xl sm:text-4xl mt-2">{t("sections.featuredTitle")}</h2>
          </div>
          <Link to={localePath("/search")} className="hidden sm:flex items-center gap-1 text-sm font-medium text-[var(--lux-seaglass)] hover:gap-2 transition-all">{t("sections.viewAll")} <ChevronRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(featured?.results || []).map((v, i) => <VillaCard key={v.id} villa={v} index={i} />)}
        </div>
      </Section>

      {/* MAP */}
      <div className="bg-secondary/40">
        <Section>
          <div className="text-center mb-8">
            <span className="eyebrow">{t("sections.mapEyebrow")}</span>
            <h2 className="text-3xl sm:text-4xl mt-2">{t("sections.mapTitle")}</h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">{t("sections.mapSubtitle")}</p>
          </div>
          <LazyVisible minHeight={480}>
            <Suspense fallback={<div className="h-[480px] rounded-[18px] bg-secondary animate-pulse" />}>
              <MapView villas={mapVillas || []} height={480} />
            </Suspense>
          </LazyVisible>
        </Section>
      </div>

      {/* CONCIERGE SERVICES */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-[20px] overflow-hidden h-[420px]">
            <img src="https://images.pexels.com/photos/6474532/pexels-photo-6474532.jpeg?auto=compress&cs=tinysrgb&w=760" alt="Luxury concierge service" loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
          <div>
            <span className="eyebrow">{t("sections.conciergeEyebrow")}</span>
            <h2 className="text-3xl sm:text-4xl mt-2">{t("sections.conciergeTitle")}</h2>
            <p className="text-muted-foreground mt-3">{t("sections.conciergeSubtitle")}</p>
            <div className="grid sm:grid-cols-2 gap-5 mt-7">
              {SERVICE_KEYS.map(([titleKey, descKey], i) => {
                const Icon = SERVICE_ICONS[i];
                return (
                  <div key={titleKey} className="flex gap-3">
                    <div className="h-11 w-11 shrink-0 rounded-xl bg-secondary flex items-center justify-center"><Icon className="h-5 w-5 text-[var(--lux-seaglass)]" /></div>
                    <div>
                      <h3 className="font-semibold">{t(titleKey)}</h3>
                      <p className="text-sm text-muted-foreground">{t(descKey)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      {/* REVIEWS */}
      {(reviews?.length || 0) > 0 && (
      <div className="bg-[var(--lux-ink)] text-[var(--lux-ivory)]">
        <Section>
          <div className="text-center mb-10">
            <span className="eyebrow text-white/60">{t("sections.reviewsEyebrow")}</span>
            <h2 className="text-3xl sm:text-4xl mt-2 text-white">{t("sections.reviewsTitle")}</h2>
          </div>
          <LazyVisible minHeight={320}>
            <Suspense fallback={<div className="h-[280px] rounded-[18px] bg-white/5 animate-pulse" />}>
              <HomeReviews reviews={reviews || []} />
            </Suspense>
          </LazyVisible>
        </Section>
      </div>
      )}

      {/* MARBELLA GUIDE / AREAS */}
      <Section>
        <div className="text-center mb-8">
          <span className="eyebrow">{t("sections.guideEyebrow")}</span>
          <h2 className="text-3xl sm:text-4xl mt-2">{t("sections.guideTitle")}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {(areas || AREAS).slice(0, 8).map((a) => (
            <Link key={a.slug} to={localePath(`/areas/${a.slug}`)} className="group relative rounded-[16px] overflow-hidden h-44 hover-lift">
              <img src={sizedImg(a.hero_image, 600, 68) || HERO_POSTER} alt={a.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-3 left-4 right-4">
                <div className="font-display text-white text-xl">{a.name}</div>
                {a.villa_count != null && <div className="text-white/70 text-xs">{a.villa_count} {t("nav.villas").toLowerCase()}</div>}
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* BLOG */}
      <div className="bg-secondary/40">
        <Section>
          <div className="flex items-end justify-between mb-8">
            <div><span className="eyebrow">{t("sections.blogEyebrow")}</span><h2 className="text-3xl sm:text-4xl mt-2">{t("sections.blogTitle")}</h2></div>
            <Link to={localePath("/blog")} className="hidden sm:flex items-center gap-1 text-sm font-medium text-[var(--lux-seaglass)]">{t("sections.allArticles")} <ChevronRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {(blog?.posts || []).map((p) => (
              <Link key={p.id} to={localePath(`/blog/${p.slug}`)} className="group bg-white rounded-[18px] overflow-hidden border border-border hover-lift">
                <div className="h-48 overflow-hidden"><img src={sizedImg(p.cover_image, 640)} alt={p.title} loading="lazy" decoding="async" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-5">
                  <span className="eyebrow text-[var(--lux-seaglass)]">{p.category}</span>
                  <h3 className="font-display text-xl mt-2 leading-tight">{p.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      </div>

      {/* FAQ */}
      <Section>
        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10">
          <div>
            <span className="eyebrow">{t("sections.faqEyebrow")}</span>
            <h2 className="text-3xl sm:text-4xl mt-2">{t("sections.faqTitle")}</h2>
            <p className="text-muted-foreground mt-3">{t("sections.faqSubtitle")}</p>
          </div>
          <Suspense fallback={<div className="h-[300px] rounded-[16px] bg-secondary animate-pulse" />}>
            <FaqAccordion faq={faqItems} />
          </Suspense>
        </div>
      </Section>

      {/* CTA */}
      <div className="container-lux pb-20">
        <div className="relative rounded-[24px] overflow-hidden">
          <img src="https://images.pexels.com/photos/37800934/pexels-photo-37800934.jpeg?auto=compress&cs=tinysrgb&w=1280" alt="Marbella villa" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
          <div className="relative text-center text-white py-20 px-6">
            <h2 className="font-display text-3xl sm:text-5xl text-white">{t("cta.title")}</h2>
            <p className="text-white/85 mt-4 max-w-xl mx-auto">{t("cta.subtitle")}</p>
            <div className="flex flex-wrap gap-3 justify-center mt-8">
              <Link to={localePath("/search")}><Button size="lg" className="bg-[var(--lux-champagne)] text-[var(--lux-ink)] hover:bg-[#d9c590] h-12 px-7">{t("cta.browse")} <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
              <Link to={localePath("/contact")}><Button size="lg" variant="outline" className="h-12 px-7 bg-white/10 text-white border-white/40 hover:bg-white/20 hover:text-white">{t("cta.talk")}</Button></Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;

